/**
 * Property Valuations Hook
 *
 * React Query hook for fetching and managing property valuation data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  propertyValuationsApi,
  type PropertyValuationRequest,
  type ValuationStatus,
} from '../api/client';

const QUERY_KEY = 'propertyValuations';
const PROVIDER_STATUS_KEY = 'propertyProviderStatus';

interface UsePropertyValuationsParams {
  status?: ValuationStatus;
  tenantId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Hook for listing property valuations
 */
export function usePropertyValuations(params?: UsePropertyValuationsParams) {
  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => propertyValuationsApi.list(params),
  });
}

/**
 * Hook for creating a new property valuation
 */
export function useCreatePropertyValuation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PropertyValuationRequest) =>
      propertyValuationsApi.create(request),
    onSuccess: () => {
      // Invalidate the valuations list to refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook for fetching property provider status
 */
export function usePropertyProviderStatus() {
  return useQuery({
    queryKey: [PROVIDER_STATUS_KEY],
    queryFn: () => propertyValuationsApi.getProviderStatus(),
    // Refresh every 30 seconds
    refetchInterval: 30000,
  });
}
